import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CubeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BuildingOfficeIcon,
  ComputerDesktopIcon,
  WrenchScrewdriverIcon,
  UserIcon,
  TagIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Modal, Button, Input, Select } from '../../../components/common';

// Redux actions
import { createTask, updateTask } from '../../../store/slices/taskSlice';

// API hooks
import { useGetUsersQuery } from '../../../services/employeeApi';
import { useGetDepartmentsQuery } from '../../../services/departmentApi';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const TaskForm = ({ open, mode, task, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    category: '',
    assignedTo: [],
    department: '',
    startDate: '',
    dueDate: '',
    estimatedHours: '',
    tags: [],
    tagInput: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // User search states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Debounce search term for backend API calls
  const debouncedSearchTerm = useDebounce(userSearchTerm, 300);

  // Fetch data for dropdowns
  const { data: usersResponse = {}, isLoading: usersLoading } = useGetUsersQuery({
    page: 1,
    limit: 20,
    search: debouncedSearchTerm || undefined // Only send search if there's a term
  });
  const { data: departmentsResponse = {}, isLoading: departmentsLoading, error: departmentsError } = useGetDepartmentsQuery();

  const users = usersResponse.data?.users || [];
  const departments = departmentsResponse.data || [];

  // Memoized selected users
  const selectedUsers = useMemo(() => {
    return users.filter(user => formData.assignedTo.includes(user._id));
  }, [users, formData.assignedTo]);

  // For search dropdown, show all results from backend (no frontend filtering needed)
  const searchResults = useMemo(() => {
    return users.slice(0, 10); // Limit to 10 results for dropdown
  }, [users]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  // Initialize form data when task changes
  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        category: task.category || '',
        assignedTo: task.assignedToUsers ? task.assignedToUsers.map(user => user._id) : (task.assignedTo || []),
        department: task.department || '',
        startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
        estimatedHours: task.estimatedHours || '',
        tags: task.tags || [],
        tagInput: '',
      });
    } else if (mode === 'create') {
      // Set default start date to today
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        category: '',
        assignedTo: [],
        department: '',
        startDate: today,
        dueDate: '',
        estimatedHours: '',
        tags: [],
        tagInput: '',
      });
    }
  }, [task, mode, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssignedToChange = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(userId)
        ? prev.assignedTo.filter(id => id !== userId)
        : [...prev.assignedTo, userId]
    }));
  };

  const handleAddTag = () => {
    const tag = formData.tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
        tagInput: ''
      }));
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Task title is required');
      return false;
    }
    if (formData.assignedTo.length === 0) {
      setError('At least one assignee is required');
      return false;
    }
    if (!formData.startDate) {
      setError('Start date is required');
      return false;
    }
    if (!formData.dueDate) {
      setError('Due date is required');
      return false;
    }
    if (new Date(formData.startDate) >= new Date(formData.dueDate)) {
      setError('Due date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        dueDate: new Date(formData.dueDate).toISOString(),
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : 0,
      };

      // Remove tagInput from submit data
      delete submitData.tagInput;

      if (mode === 'create') {
        await dispatch(createTask(submitData)).unwrap();
      } else {
        await dispatch(updateTask({ taskId: task._id, updates: submitData })).unwrap();
      }

      onSuccess();
    } catch (error) {
      setError(error.message || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={mode === 'create' ? 'Create New Task' : 'Edit Task'}
      size="lg"
    >
      <div className="max-w-4xl mx-auto p-2">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-blue-100 rounded-md">
              <CubeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{mode === 'create' ? 'Create New Task' : 'Edit Task'}</h2>
              <p className="text-xs text-gray-600">{mode === 'create' ? 'Assign tasks and track progress across your team' : 'Update task details and assignments'}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Task Title */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CubeIcon className="h-4 w-4 text-blue-600" />
                  <label className="text-sm font-semibold text-gray-800">
                    Task Title <span className="text-red-500">*</span>
                  </label>
                </div>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter task title"
                  required
                  className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-4 w-4 text-orange-600" />
                  <label className="text-sm font-semibold text-gray-800">
                    Priority <span className="text-red-500">*</span>
                  </label>
                </div>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  required
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <ComputerDesktopIcon className="h-4 w-4 text-green-600" />
                  <label className="text-sm font-semibold text-gray-800">
                    Status <span className="text-red-500">*</span>
                  </label>
                </div>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  required
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <WrenchScrewdriverIcon className="h-4 w-4 text-purple-600" />
                  <label className="text-sm font-semibold text-gray-800">Category</label>
                </div>
                <Input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  placeholder="Enter category"
                  className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
              </div>

              {/* Department */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-teal-600" />
                  <label className="text-sm font-semibold text-gray-800">Department</label>
                </div>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  disabled={departmentsLoading || departmentsError || departments.length === 0}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                >
                  <option value="">
                    {departmentsLoading
                      ? 'Loading departments...'
                      : departmentsError
                      ? 'Error loading departments'
                      : 'No Department'
                    }
                  </option>
                  {Array.isArray(departments) && departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estimated Hours */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-4 w-4 text-indigo-600" />
                  <label className="text-sm font-semibold text-gray-800">Estimated Hours</label>
                </div>
                <Input
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
                  placeholder="Enter estimated hours"
                  min="0"
                  step="0.5"
                  className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Timeline</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Start Date */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                  <label className="text-sm font-semibold text-gray-800">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                </div>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  required
                  className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <CalendarDaysIcon className="h-4 w-4 text-amber-600" />
                  <label className="text-sm font-semibold text-gray-800">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                </div>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  required
                  className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Description</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <ComputerDesktopIcon className="h-4 w-4 text-cyan-600" />
                <label className="text-sm font-semibold text-gray-800">Task Description</label>
              </div>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm resize-vertical"
                placeholder="Describe the task requirements and objectives..."
              />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Tags</h3>
              <span className="text-xs text-gray-500">{formData.tags.length} tags</span>
            </div>
            <div className="space-y-3">
              <div className="flex space-x-2">
                <Input
                  type="text"
                  value={formData.tagInput}
                  onChange={(e) => handleInputChange('tagInput', e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a tag and press Enter"
                  className="flex-1 text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={!formData.tagInput.trim()}
                  className="px-4 py-3 bg-pink-50 hover:bg-pink-100 disabled:bg-gray-50 text-pink-700 disabled:text-gray-400 border-2 border-pink-200 disabled:border-gray-200 rounded-lg transition-all duration-200 hover:scale-105 disabled:hover:scale-100 shadow-sm hover:shadow-md disabled:shadow-none"
                >
                  <TagIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Tags Display */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  {formData.tags.map((tag) => (
                    <div key={tag} className="flex items-center space-x-2 bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:bg-pink-200 rounded-full p-0.5 transition-colors duration-200"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Assigned Users */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Team Assignment</h3>
              <span className="text-xs text-gray-500">{formData.assignedTo.length} assigned</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <UserIcon className="h-4 w-4 text-blue-600" />
                <label className="text-sm font-semibold text-gray-800">
                  Assign to Users <span className="text-red-500">*</span>
                </label>
              </div>

              {/* User Search and Selection */}
              <div className="space-y-3 user-search-container">
                {/* Search Input */}
                <div className="relative">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      placeholder="Search users by name, email, or role..."
                      className="pl-10 text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Search Dropdown */}
                  {showUserDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {usersLoading ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          Loading users...
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No users found matching "{userSearchTerm}"
                        </div>
                      ) : (
                        <div className="py-1">
                          {searchResults.map((user) => {
                            const isSelected = formData.assignedTo.includes(user._id);
                            return (
                              <div
                                key={user._id}
                                onClick={() => {
                                  handleAssignedToChange(user._id);
                                  setUserSearchTerm('');
                                  setShowUserDropdown(false);
                                }}
                                className={`flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                  isSelected ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div className="flex items-center space-x-3 flex-1">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                                    isSelected ? 'bg-blue-500' : 'bg-gray-400'
                                  }`}>
                                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {user.fullName}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {user.email} • {user.role}
                                    </p>
                                  </div>
                                </div>
                                {isSelected && (
                                  <CheckIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Users Display */}
                {formData.assignedTo.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
                    {formData.assignedTo.map((userId) => {
                      const user = users.find(u => u._id === userId);
                      if (!user) return null;
                      return (
                        <div key={userId} className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          <span>{user.fullName}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newAssignedTo = formData.assignedTo.filter(id => id !== userId);
                              handleInputChange('assignedTo', newAssignedTo);
                            }}
                            className="hover:bg-blue-200 rounded-full p-0.5 transition-colors duration-200"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {formData.assignedTo.length === 0 && (
                <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <svg className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                    </svg>
                    <p>Please select at least one user to assign this task to.</p>
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Backend Search & Selection:</p>
                    <ul className="space-y-1 text-gray-600">
                      <li>• Type to search users (debounced backend search)</li>
                      <li>• Searches by name, email, and role in database</li>
                      <li>• Click on users to select/deselect them</li>
                      <li>• Selected users appear as removable tags below</li>
                      <li>• Click outside to close the search dropdown</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? 'Creating Task...' : (mode === 'create' ? 'Create Task' : 'Update Task')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TaskForm;
